/**
 * 映像エフェクト (シーン単位・時間窓付き) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-S。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出しの注記)
 * - video-editor リポジトリの src/schema/effects.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/effects.py (AI 提案の正規化・部分操作・要約・同値テスト)
 *
 * ## 形
 *
 * `Effect = {id, kind, startSec?, endSec?, intensity, seed?}`
 *
 * - `kind`: `shake` (画面揺れ) / `zoom_punch` (瞬間の寄り) / `flash` (白フラッシュ) /
 *   `glitch` (デジタルノイズ) / `light_leak` (光漏れ) / `grain` (フィルムグレイン) / `vhs`
 * - `startSec` / `endSec`: **そのシーンの表示秒**を基準にした窓 (テロップ・シーンのオーバーレイと
 *   同じ基準)。**両方省略 = シーン全体**
 * - `intensity`: 強さ 0.1〜1.0 (0.05 刻み・既定 0.5)。正規化で必ず埋める (保存値 = 実効値)
 * - `seed`: 決定的乱数の種 (整数)。省略時は `id` から導出する
 *
 * 1 シーンに **4 つまで**で、**同じ kind は正規化で後勝ちの 1 つに畳む**
 * (「同じ揺れを二重に掛ける」指定を値空間から消し、強さ 1 本で表現させるため)。
 *
 * ## 時間モデル
 *
 * 窓は表示時間軸 (フリーズ / 速度ランプ適用後の表示秒)。トリム / 速度 / 分割の追従は
 * **オーバーレイ・テロップとまったく同じ規則**で、実体は schema/timedSpan.ts の共通ロジック
 * (時間の式をここで書き直さない)。
 *
 * ## 決定的乱数 (3 実装同値の要)
 *
 * 揺れ・グリッチ・グレインはフレームごとに値が変わるので、`Math.random` は使えない
 * (プレビューと MP4 で絵がずれ、同値テストも書けない)。**32bit 整数演算だけの xorshift**
 * (`effectRandom`) を 3 実装に置き、JS は `Math.imul` (`a * b` は 2^53 を超えて誤差が出る)、
 * Python は `& 0xFFFFFFFF` で 32bit に揃える。周期のあるものは「フレーム番号」ではなく
 * **離散化したステップ** (`floor(表示秒 × 周波数)`) を入力にするので、fps が変わっても
 * 見た目の周期は変わらない (グレインだけは毎フレーム変えるので frame を直接使う)。
 *
 * ## 描画の層 (FE = レンダラー)
 *
 * - `transform` … **look 層の外側 (シーン層の直下・トランジションの変形の内側)** に 1 枚。
 *   映像 + シーンのオーバーレイ + tint / ビネットを一緒に揺らす (テロップは揺らさない)
 * - `layers` … **シーン層の中・look 層の上**。テロップ (CaptionTrack) は全体層にあるので、
 *   光学層は必ずその下になる (テロップの上に被せると文字が読めなくなるため)
 * - 窓の外では**要素を足さない** (`transform` が空・`layers` が空 = 従来と同じ DOM)
 */
import { z } from 'zod';
import {
  clampSpansToDuration,
  isWholeSpan,
  rescaleSpansForSpeed,
  roundHalfUp,
  splitSpans,
} from './timedSpan';
import { formatDurationLabel, formatTimecode } from './timecode';

export const EFFECT_KINDS = [
  'shake',
  'zoom_punch',
  'flash',
  'glitch',
  'light_leak',
  'grain',
  'vhs',
] as const;
export type EffectKind = (typeof EFFECT_KINDS)[number];

export const EFFECT_KIND_LABELS: Record<EffectKind, string> = {
  shake: '揺れ',
  zoom_punch: 'ズームパンチ',
  flash: 'フラッシュ',
  glitch: 'グリッチ',
  light_leak: '光漏れ',
  grain: 'フィルムグレイン',
  vhs: 'VHS',
};

/** 1 シーンに置けるエフェクトの上限 (種類が違っても 4 つまで)。 */
export const MAX_EFFECTS_PER_ITEM = 4;

/** 強さの範囲・刻み・既定値。 */
export const EFFECT_INTENSITY_MIN = 0.1;
export const EFFECT_INTENSITY_MAX = 1;
export const EFFECT_INTENSITY_STEP = 0.05;
export const EFFECT_INTENSITY_DEFAULT = 0.5;

/** lint のしきい値: 1 シーンにこれ以上あったら「多すぎ」。 */
export const EFFECTS_PER_SCENE_WARN_COUNT = 3;
/** lint のしきい値: 動画全体で zoom_punch + flash がこれ以上あったら「多すぎ」。 */
export const PULSE_EFFECT_WARN_COUNT = 6;

/**
 * パルス系 (窓の先頭で一瞬だけ効く) の効く長さ。`endSec` を書いたときだけ窓の長さを
 * min / max にクランプして使い、書いていなければ `def` を使う (窓を省略しても
 * 「シーン全体が真っ白」にはならない)。ここに無い kind は窓いっぱい効く。
 */
export const EFFECT_PULSE_SEC: Partial<Record<EffectKind, { min: number; max: number; def: number }>> = {
  zoom_punch: { min: 0.1, max: 1, def: 0.3 },
  flash: { min: 0.05, max: 0.5, def: 0.2 },
};

/** ops (`add_effect`) で `durationSec` を省略したときに作る窓の長さ (null = シーン全体)。 */
export const EFFECT_DEFAULT_WINDOW_SEC: Record<EffectKind, number | null> = {
  shake: 0.6,
  zoom_punch: 0.3,
  flash: 0.2,
  glitch: 0.5,
  light_leak: null,
  grain: null,
  vhs: null,
};

// ── 描画のパラメータ (3 実装で同値) ───────────────────────────────────────────
/** 揺れ: 1 秒あたりの揺れ直し回数 (表示秒で離散化する)。 */
export const SHAKE_HZ = 12;
/** 揺れ: 強さ 1.0 での平行移動の振幅 (出力の幅 / 高さに対する %)。 */
export const SHAKE_MAX_TRANSLATE_PERCENT = 2;
/** 揺れ: 強さ 1.0 での回転の振幅 (度)。 */
export const SHAKE_MAX_ROTATE_DEG = 1.5;
/** 揺れ: 端に黒が出ないよう拡大する量 (強さ 1.0 で 1.10 倍)。 */
export const SHAKE_SCALE_MARGIN = 0.1;

/** ズームパンチ: 強さ 1.0 での拡大量 (1.12 倍)。 */
export const ZOOM_PUNCH_PEAK = 0.12;
/**
 * ズームパンチ: 頂点まで跳ねる時間 (秒)。
 * **フレーム数ではなく秒で持つ** — フレーム数だと fps が違うだけで跳ねの速さが変わり、
 * 24fps と 30fps で別の演出になってしまう (3 実装同値テストも fps ごとに分かれる)。
 */
export const ZOOM_PUNCH_RISE_SEC = 0.07;

/** フラッシュ: 強さ 1.0 での白のピーク不透明度。 */
export const FLASH_PEAK_OPACITY = 0.9;

/** グリッチ: 帯が出直す周波数 (強さで 6→24Hz)。 */
export const GLITCH_HZ_MIN = 6;
export const GLITCH_HZ_MAX = 24;
/** グリッチ: この値未満の乱数が出たステップだけ帯を描く (間欠にする)。 */
export const GLITCH_BURST_THRESHOLD = 0.45;
/** グリッチ: 1 回に出る帯の最大本数。 */
export const GLITCH_MAX_SLICES = 4;
/** グリッチ: 強さ 1.0 での水平ずれ (出力幅に対する %) と帯の高さ (出力高さに対する %)。 */
export const GLITCH_MAX_SHIFT_PERCENT = 7;
export const GLITCH_MAX_BAND_PERCENT = 9;

/** 光漏れ: 強さ 0 / 1 での不透明度。 */
export const LIGHT_LEAK_BASE_OPACITY = 0.25;
export const LIGHT_LEAK_INTENSITY_RANGE = 0.35;

/** グレイン: 強さ 0 / 1 での不透明度と、ノイズのタイルサイズ (px)。 */
export const GRAIN_BASE_OPACITY = 0.06;
export const GRAIN_INTENSITY_RANGE = 0.14;
export const GRAIN_TILE_PX = 160;

/** VHS: 縦揺れの周波数・振幅 (%) と拡大の余白。 */
export const VHS_HZ = 8;
export const VHS_MAX_TRANSLATE_PERCENT = 0.5;
export const VHS_SCALE_MARGIN = 0.02;
/** VHS: 走査線・色フリンジ・ノイズの不透明度 (基準 + 強さぶん)。 */
export const VHS_SCANLINE_BASE_OPACITY = 0.18;
export const VHS_SCANLINE_INTENSITY_RANGE = 0.3;
export const VHS_CHROMA_BASE_OPACITY = 0.12;
export const VHS_CHROMA_INTENSITY_RANGE = 0.25;
export const VHS_NOISE_BASE_OPACITY = 0.04;
export const VHS_NOISE_INTENSITY_RANGE = 0.1;

export const EffectSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(EFFECT_KINDS),
  /** 表示区間 (そのシーンの表示秒)。省略 = シーン全体。 */
  startSec: z.number().nonnegative().optional(),
  endSec: z.number().nonnegative().optional(),
  /**
   * 範囲をスキーマで縛らない (OverlaySchema と同じ方針): 外から書き換えられた JSON で
   * プロジェクト全体が開けなくなるのを避け、クランプは normalizeEffect が行う。
   */
  intensity: z.number().optional(),
  seed: z.number().optional(),
});
export type Effect = z.infer<typeof EffectSchema>;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

// ── 決定的乱数 (xorshift32) ──────────────────────────────────────────────────

/** 32bit の混ぜ込み (Math.imul = 32bit 乗算。Python の `& 0xFFFFFFFF` と同値)。 */
function mix32(a: number, b: number): number {
  return (Math.imul(a, b) >>> 0);
}

/**
 * 決定的な擬似乱数 (整数)。`seed` と 2 つの入力 (ステップ・チャンネル) から 32bit 整数を返す。
 * **3 実装で 1 ビットも違わないこと**が要件なので、32bit 整数演算だけで書く。
 */
export function effectRandomInt(seed: number, a: number, b: number): number {
  let x = (seed >>> 0) ^ mix32(a >>> 0, 2654435761) ^ mix32(b >>> 0, 40503);
  x = x >>> 0;
  // 0 は xorshift の不動点なので避ける
  if (x === 0) x = 0x9e3779b9;
  x = (x ^ (x << 13)) >>> 0;
  x = (x ^ (x >>> 17)) >>> 0;
  x = (x ^ (x << 5)) >>> 0;
  return x >>> 0;
}

/** 決定的な擬似乱数 (0 <= r < 1)。 */
export function effectRandom(seed: number, a: number, b: number): number {
  return effectRandomInt(seed, a, b) / 4294967296;
}

/**
 * 三角関数を使わない滑らかな波 (位相 0〜1 → -1〜1)。
 * `Math.sin` は処理系で最後の 1 ulp が違いうるので、丸めの境界で 3 実装が割れないよう
 * 四則演算と `floor` だけで作る。
 */
export function effectWave(phase: number): number {
  const t = phase - Math.floor(phase);
  const tri = t < 0.5 ? 4 * t - 1 : 3 - 4 * t;
  return tri * (2 - Math.abs(tri));
}

/** `id` から決定的に種を作る (seed 省略時のフォールバック)。 */
export function effectSeedFromId(id: string): number {
  let hash = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash ^ id.charCodeAt(i)) >>> 0;
    hash = mix32(hash, 16777619);
  }
  return hash >>> 0;
}

/** 実効の種。 */
export function resolveEffectSeed(effect: Pick<Effect, 'id' | 'seed'>): number {
  const seed = num(effect.seed);
  if (seed === undefined) return effectSeedFromId(effect.id ?? '');
  return Math.abs(Math.trunc(seed)) >>> 0;
}

/** 実効の強さ (保存形は必ず持つが、旧データ・壊れた値のための保険)。 */
export function resolveEffectIntensity(effect: Pick<Effect, 'intensity'>): number {
  const value = num(effect.intensity);
  if (value === undefined) return EFFECT_INTENSITY_DEFAULT;
  const snapped = roundHalfUp(value / EFFECT_INTENSITY_STEP, 0) * EFFECT_INTENSITY_STEP;
  return roundHalfUp(clamp(snapped, EFFECT_INTENSITY_MIN, EFFECT_INTENSITY_MAX), 2);
}

// ── 窓 ───────────────────────────────────────────────────────────────────────

export interface EffectSpan {
  startSec: number;
  endSec: number;
}

/** 指定された窓 (省略 = シーン全体) を表示尺の内側へ収めたもの。 */
export function effectWindow(effect: Effect, durationSec: number): EffectSpan {
  const start = clamp(effect.startSec ?? 0, 0, durationSec);
  const end = clamp(effect.endSec ?? durationSec, 0, durationSec);
  return { startSec: roundHalfUp(start, 4), endSec: roundHalfUp(Math.max(start, end), 4) };
}

/**
 * 実際に効く区間。パルス系 (zoom_punch / flash) は「窓の先頭から既定 / クランプした長さ」だけ効く
 * (窓を省略しても 1 シーンぶん光り続けない)。
 */
export function effectActiveSpan(effect: Effect, durationSec: number): EffectSpan {
  const window = effectWindow(effect, durationSec);
  const pulse = EFFECT_PULSE_SEC[effect.kind];
  if (!pulse) return window;
  const length = effect.endSec !== undefined
    ? clamp(window.endSec - window.startSec, pulse.min, pulse.max)
    : pulse.def;
  return {
    startSec: window.startSec,
    endSec: roundHalfUp(Math.min(durationSec, window.startSec + length), 4),
  };
}

/**
 * パルス系 (zoom_punch / flash) が実際に効く長さ (秒)。パルス系でなければ undefined。
 *
 * `durationSec` を渡すと「窓の先頭からシーンの終わりまで」でも頭打ちにする
 * (要約・UI に**実効の長さ**を出すためのもので、`effectActiveSpan` と同じ値になる)。
 */
export function effectPulseLengthSec(
  effect: Pick<Effect, 'kind' | 'startSec' | 'endSec'>,
  durationSec?: number,
): number | undefined {
  const pulse = EFFECT_PULSE_SEC[effect.kind];
  if (!pulse) return undefined;
  const start = effect.startSec ?? 0;
  let length = effect.endSec !== undefined
    ? clamp(effect.endSec - start, pulse.min, pulse.max)
    : pulse.def;
  if (durationSec !== undefined) length = Math.min(length, Math.max(0, durationSec - start));
  return roundHalfUp(length, 2);
}

/** その時刻にエフェクトが効いているか。 */
export function isEffectActiveAt(effect: Effect, sec: number, durationSec: number): boolean {
  const span = effectActiveSpan(effect, durationSec);
  return span.endSec > span.startSec && sec >= span.startSec && sec < span.endSec;
}

/** 窓を持つか (シーン全体に掛かっていないか)。lint が「窓なしの揺れ」を見るのに使う。 */
export function hasEffectWindow(effect: Pick<Effect, 'startSec' | 'endSec'>): boolean {
  return !isWholeSpan(effect);
}

// ── 正規化 ───────────────────────────────────────────────────────────────────

/**
 * 外部入力 (AI の提案・古い保存データ) の 1 件を保存形へ正規化する。
 * 落とすのは「kind が不明」「id が無い」の 2 つだけ (数値は丸めてクランプする)。
 */
export function normalizeEffect(value: unknown, fallbackId?: string): Effect | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const kind = raw.kind;
  if (typeof kind !== 'string' || !(EFFECT_KINDS as readonly string[]).includes(kind)) return undefined;
  const id =
    typeof raw.id === 'string' && raw.id.length > 0
      ? raw.id
      : typeof fallbackId === 'string' && fallbackId.length > 0
        ? fallbackId
        : undefined;
  if (!id) return undefined;

  const next: Effect = {
    id,
    kind: kind as EffectKind,
    // 保存値 = 実効値。既定 (0.5) でも書く (AI / UI が読んだ値がそのまま効く)
    intensity: resolveEffectIntensity(raw as Pick<Effect, 'intensity'>),
  };

  const startRaw = num(raw.startSec);
  const endRaw = num(raw.endSec);
  const startSec = startRaw !== undefined && startRaw >= 0 ? roundHalfUp(startRaw, 2) : undefined;
  const endSec = endRaw !== undefined && endRaw >= 0 ? roundHalfUp(endRaw, 2) : undefined;
  // end <= start は区間として成立しないので、両方落として「シーン全体」に倒す。
  // start を省略した (= 0) ときも同じ扱いにする (`{endSec: 0}` を「長さ 0 の窓」として残さない)。
  if (!(endSec !== undefined && endSec <= (startSec ?? 0))) {
    if (startSec !== undefined && startSec > 0) next.startSec = startSec;
    if (endSec !== undefined) next.endSec = endSec;
  }

  const seed = num(raw.seed);
  if (seed !== undefined) next.seed = Math.abs(Math.trunc(seed)) >>> 0;
  return next;
}

/**
 * 配列の正規化。**同じ kind は後勝ちで 1 つに畳み**、上限 (4 件) を超えた分は落とす。
 * 落ちた件数は dropped に積む (AI へのフィードバックに使う)。
 */
export function normalizeEffects(
  value: unknown,
  makeId?: (index: number) => string,
): { effects: Effect[]; dropped: number } {
  if (!Array.isArray(value)) return { effects: [], dropped: 0 };
  const byKind = new Map<EffectKind, Effect>();
  let dropped = 0;
  value.forEach((raw, index) => {
    const normalized = normalizeEffect(raw, makeId ? makeId(index) : defaultEffectId());
    if (!normalized) {
      dropped += 1;
      return;
    }
    // 後勝ち。位置も最後に書かれたところへ移す (Map の再挿入で順序が変わるよう delete してから set)
    if (byKind.has(normalized.kind)) {
      byKind.delete(normalized.kind);
      dropped += 1;
    }
    byKind.set(normalized.kind, normalized);
  });
  const all = Array.from(byKind.values());
  const effects = all.slice(0, MAX_EFFECTS_PER_ITEM);
  dropped += all.length - effects.length;
  return { effects, dropped };
}

/**
 * `id` を持たない入力 (AI の提案・手書きの JSON) に振る既定の id。
 * backend の `normalize_effects` が `uuid4()` を振るのと同じ意味。
 */
function defaultEffectId(): string {
  const uuid = (globalThis.crypto as Crypto | undefined)?.randomUUID;
  return typeof uuid === 'function'
    ? uuid.call(globalThis.crypto)
    : `fx-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 読み込み時にエフェクト配列を保存形へ揃える (zod の前処理)。
 *
 * **スキーマで throw させない**のが要点 (1 件の壊れた要素でプロジェクトごと開けなく = MP4 も
 * 出せなくしない。§0-2)。オーバーレイ (`dropUnusableOverlayEntries`) は「使えない要素を落とす」
 * だけだが、エフェクトは **`normalizeEffects` の結果で置き換える**: 型の壊れた強さ・同じ種類の
 * 重複・5 件目といった「スキーマは通るが契約に反する」入力も、ここで保存形へ畳んでおかないと
 * `z.array(EffectSchema)` を通り抜けて描画・要約がおかしくなる (id 無しの要素も id を振って残す)。
 * 保存時は BE の `validate_edit_project_json` が 400 で弾く。
 */
export function dropUnusableEffectEntries(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  const { effects, dropped } = normalizeEffects(value);
  if (dropped > 0) {
    console.warn(
      `[videoEditor] 使えない映像エフェクト ${dropped} 件を読み込み時に除外しました`,
    );
  }
  return effects;
}

/** シーンに載る配列のスキーマ (壊れた要素は前処理で落とす)。 */
export const EffectArraySchema = z.preprocess(dropUnusableEffectEntries, z.array(EffectSchema));

// ── 表示・要約 ───────────────────────────────────────────────────────────────

/** 0.1 刻みの値を「0.6」「2」の形で書く (末尾の .0 を落とす)。 */
function fmt(value: number): string {
  return String(roundHalfUp(value, 2));
}

/**
 * AI / UI に見せる 1 行の説明 (「揺れ 0.6 (0〜1.2秒)」「フラッシュ (2.4秒〜・0.5秒)」)。
 * 強さは既定 (0.5) のときは書かない (要約を短く保つ)。
 *
 * **パルス系 (zoom_punch / flash) は保存した窓ではなく実効スパンを書く**: 窓をそのまま印字すると
 * 「フラッシュ (1〜3秒)」のように 2 秒光るように読めてしまうが、実際は上限 (0.5 秒) で切られる。
 * `durationSec` (シーンの表示尺) を渡すと、シーン末尾での頭打ちも反映する。
 * `fps` を渡すと画面向けに書く (位置は "MM:SS:FF"、長さは "S:FF" (秒:フレーム))。
 * 省略時は秒のまま (AI の要約・backend の文言と揃える)。
 */
export function describeEffect(effect: Effect, durationSec?: number, fps?: number): string {
  const parts: string[] = [EFFECT_KIND_LABELS[effect.kind]];
  const intensity = resolveEffectIntensity(effect);
  if (Math.abs(intensity - EFFECT_INTENSITY_DEFAULT) > 1e-9) parts.push(fmt(intensity));
  const start = effect.startSec ?? 0;
  const pulseLength = effectPulseLengthSec(effect, durationSec);
  if (fps !== undefined) {
    const at = (sec: number) => formatTimecode(sec, fps);
    if (pulseLength !== undefined) parts.push(`(${at(start)}〜・${formatDurationLabel(pulseLength, fps)})`);
    else if (effect.endSec !== undefined) parts.push(`(${at(start)}〜${at(effect.endSec)})`);
    else if (effect.startSec !== undefined) parts.push(`(${at(start)}〜)`);
    else parts.push('(シーン全体)');
    return parts.join(' ');
  }
  if (pulseLength !== undefined) parts.push(`(${fmt(start)}秒〜・${fmt(pulseLength)}秒)`);
  else if (effect.endSec !== undefined) parts.push(`(${fmt(start)}〜${fmt(effect.endSec)}秒)`);
  else if (effect.startSec !== undefined) parts.push(`(${fmt(start)}秒〜)`);
  else parts.push('(シーン全体)');
  return parts.join(' ');
}

/** 要約の 1 行 (「揺れ 0.6 (0〜1.2秒) / フラッシュ (2.4秒〜・0.2秒)」)。空配列なら空文字。 */
export function describeEffects(effects: Effect[] | undefined, durationSec?: number, fps?: number): string {
  const list = (effects ?? []).filter((effect) => effect && typeof effect === 'object');
  return list.map((effect) => describeEffect(effect, durationSec, fps)).join(' / ');
}

// ── 区間の追従 (実体は schema/timedSpan.ts) ──────────────────────────────────

/** 新しい表示尺に合わせて窓を詰める (成立しなくなったものは落とす)。 */
export function clampEffectsToDuration(effects: Effect[], durationSec: number): Effect[] {
  return clampSpansToDuration(effects, durationSec).kept;
}

/** 速度変更で表示尺が変わったときの詰め直し (窓を持たないものは触らない)。 */
export function rescaleEffectsForSpeed(
  effects: Effect[] | undefined,
  prevSpeed: number,
  nextSpeed: number,
): Effect[] | undefined {
  return rescaleSpansForSpeed(effects, prevSpeed, nextSpeed);
}

/** 表示秒 atSec で分割したときの前半 / 後半 (窓を持たないものは両方に複製)。 */
export function splitEffects(
  effects: Effect[] | undefined,
  atSec: number,
  makeId: (index: number) => string,
): { first: Effect[]; second: Effect[] } {
  return splitSpans(effects, atSec, makeId);
}

// ── 評価 (描画の正本) ────────────────────────────────────────────────────────

/** `evaluateEffects` に渡す文脈。表示秒は `frame / fps` で内部的に導出する (式を二重に持たない)。 */
export interface EffectsContext {
  /** シーンの先頭を 0 とした表示フレーム。 */
  frame: number;
  fps: number;
  /** シーンの表示尺 (秒)。窓を省略したエフェクトはこの全体に掛かる。 */
  durationSec: number;
  /** 出力の幅 / 高さ (px)。走査線の太さなどに使う。 */
  width: number;
  height: number;
}

/** 光学系の重ね 1 枚。`style` はそのまま AbsoluteFill に渡す。 */
export interface EffectLayer {
  id: string;
  kind: EffectKind;
  style: Record<string, string | number>;
}

export interface EffectsRenderState {
  /** シーン層 (映像 + シーンのオーバーレイ) に掛ける transform。空文字 = 変形なし。 */
  transform: string;
  /** テロップの下・look の上に重ねる層 (描く順)。 */
  layers: EffectLayer[];
}

/** 何も効いていない状態 (この値のときは層を足さない = 従来と同じ DOM)。 */
export const EFFECTS_IDENTITY: EffectsRenderState = { transform: '', layers: [] };

/** SVG の feTurbulence ノイズを敷く background-image (グレイン / VHS のノイズ共通)。 */
function noiseBackgroundImage(noiseSeed: number): string {
  const size = GRAIN_TILE_PX;
  return (
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'`
    + `%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'`
    + ` seed='${noiseSeed}'/%3E%3C/filter%3E%3Crect width='${size}' height='${size}'`
    + ` filter='url(%23n)'/%3E%3C/svg%3E")`
  );
}

/**
 * そのフレームで効くエフェクトを評価する (描画の正本)。
 *
 * 戻り値の数値はすべて丸め済みなので、3 実装の出力を**そのまま比較**できる。
 * 窓の外のエフェクトは一切寄与しない (`EFFECTS_IDENTITY` と同じ値になる)。
 */
export function evaluateEffects(
  effects: Effect[] | undefined,
  ctx: EffectsContext,
): EffectsRenderState {
  const list = (effects ?? []).filter((effect) => effect && typeof effect === 'object');
  if (list.length === 0) return EFFECTS_IDENTITY;
  const fps = ctx.fps > 0 ? ctx.fps : 30;
  const sec = ctx.frame / fps;
  const transforms: string[] = [];
  const layers: EffectLayer[] = [];

  // 種類の順に評価する (配列の並びに依らず transform の合成順を一定にするため)
  for (const kind of EFFECT_KINDS) {
    // 同じ kind が複数あれば後勝ち (normalizeEffects と同じ規則)
    let effect: Effect | undefined;
    for (const candidate of list) {
      if (candidate.kind === kind) effect = candidate;
    }
    if (!effect) continue;
    const span = effectActiveSpan(effect, ctx.durationSec);
    const total = span.endSec - span.startSec;
    if (!(total > 0) || sec < span.startSec || sec >= span.endSec) continue;
    const i = resolveEffectIntensity(effect);
    const seed = resolveEffectSeed(effect);
    const elapsed = sec - span.startSec;
    const progress = total > 0 ? clamp(elapsed / total, 0, 1) : 0;

    if (kind === 'shake') {
      const step = Math.floor(sec * SHAKE_HZ);
      const dx = roundHalfUp((effectRandom(seed, step, 1) * 2 - 1) * SHAKE_MAX_TRANSLATE_PERCENT * i, 2);
      const dy = roundHalfUp((effectRandom(seed, step, 2) * 2 - 1) * SHAKE_MAX_TRANSLATE_PERCENT * i, 2);
      const rot = roundHalfUp((effectRandom(seed, step, 3) * 2 - 1) * SHAKE_MAX_ROTATE_DEG * i, 2);
      const scale = roundHalfUp(1 + SHAKE_SCALE_MARGIN * i, 4);
      transforms.push(`translate(${dx}%, ${dy}%) rotate(${rot}deg) scale(${scale})`);
    } else if (kind === 'zoom_punch') {
      const riseSec = ZOOM_PUNCH_RISE_SEC;
      const peak = ZOOM_PUNCH_PEAK * i;
      let scale: number;
      if (elapsed < riseSec) {
        scale = 1 + peak * (riseSec > 0 ? elapsed / riseSec : 1);
      } else {
        const rest = Math.max(total - riseSec, 1 / fps);
        const q = clamp((elapsed - riseSec) / rest, 0, 1);
        // ease-out で 1 へ戻す (戻り量 = peak × (1-q)^3)
        scale = 1 + peak * (1 - q) * (1 - q) * (1 - q);
      }
      transforms.push(`scale(${roundHalfUp(scale, 4)})`);
    } else if (kind === 'flash') {
      const opacity = roundHalfUp(FLASH_PEAK_OPACITY * i * (1 - progress), 4);
      if (opacity > 0) {
        layers.push({
          id: `${effect.id}-flash`,
          kind,
          style: { backgroundColor: '#FFFFFF', opacity },
        });
      }
    } else if (kind === 'glitch') {
      const hz = GLITCH_HZ_MIN + (GLITCH_HZ_MAX - GLITCH_HZ_MIN) * i;
      const step = Math.floor(sec * hz);
      if (effectRandom(seed, step, 10) < GLITCH_BURST_THRESHOLD) {
        const slices = 2 + Math.floor(effectRandom(seed, step, 11) * (GLITCH_MAX_SLICES - 1));
        for (let k = 0; k < slices; k += 1) {
          const y = roundHalfUp(effectRandom(seed, step, 20 + k * 4) * 92, 2);
          const rawHeight = 1.5 + effectRandom(seed, step, 21 + k * 4) * GLITCH_MAX_BAND_PERCENT * i;
          const h = roundHalfUp(Math.min(rawHeight, 100 - y), 2);
          const dx = roundHalfUp(
            (effectRandom(seed, step, 22 + k * 4) * 2 - 1) * GLITCH_MAX_SHIFT_PERCENT * i, 2,
          );
          const warm = effectRandom(seed, step, 23 + k * 4) < 0.5;
          layers.push({
            id: `${effect.id}-glitch-${k}`,
            kind,
            style: {
              clipPath: `inset(${y}% 0% ${roundHalfUp(100 - y - h, 2)}% 0%)`,
              transform: `translateX(${dx}%)`,
              // 真の RGB 分離 (映像の複製) は採らない近似 (§4.1-S の既知の制限)
              backgroundColor: warm ? 'rgba(255, 0, 72, 0.35)' : 'rgba(0, 200, 255, 0.35)',
              mixBlendMode: 'screen',
            },
          });
        }
      }
    } else if (kind === 'light_leak') {
      const offset = effectRandom(seed, 0, 7);
      const cx = roundHalfUp(50 + 40 * effectWave(progress * 0.7 + offset), 2);
      const cy = roundHalfUp(50 + 25 * effectWave(progress * 0.5 + offset + 0.25), 2);
      const base = LIGHT_LEAK_BASE_OPACITY + LIGHT_LEAK_INTENSITY_RANGE * i;
      const opacity = roundHalfUp(
        clamp(base * (0.65 + 0.35 * effectWave(progress * 1.3 + offset)), 0, 1), 4,
      );
      layers.push({
        id: `${effect.id}-leak`,
        kind,
        style: {
          backgroundImage:
            `radial-gradient(circle at ${cx}% ${cy}%, rgba(255, 214, 170, 0.95) 0%,`
            + ' rgba(255, 150, 70, 0.45) 35%, rgba(255, 110, 40, 0) 70%)',
          mixBlendMode: 'screen',
          opacity,
        },
      });
    } else if (kind === 'grain') {
      const noiseSeed = effectRandomInt(seed, ctx.frame, 8) % 1000;
      layers.push({
        id: `${effect.id}-grain`,
        kind,
        style: {
          backgroundImage: noiseBackgroundImage(noiseSeed),
          backgroundSize: `${GRAIN_TILE_PX}px ${GRAIN_TILE_PX}px`,
          mixBlendMode: 'overlay',
          opacity: roundHalfUp(GRAIN_BASE_OPACITY + GRAIN_INTENSITY_RANGE * i, 4),
        },
      });
    } else if (kind === 'vhs') {
      const step = Math.floor(sec * VHS_HZ);
      const dy = roundHalfUp((effectRandom(seed, step, 4) * 2 - 1) * VHS_MAX_TRANSLATE_PERCENT * i, 2);
      const scale = roundHalfUp(1 + VHS_SCALE_MARGIN * i, 4);
      transforms.push(`translate(0%, ${dy}%) scale(${scale})`);
      const linePx = Math.max(1, Math.round(ctx.height / 540));
      const periodPx = linePx * 3;
      layers.push({
        id: `${effect.id}-vhs-scanline`,
        kind,
        style: {
          backgroundImage:
            `repeating-linear-gradient(to bottom, rgba(0, 0, 0, 0.45) 0px,`
            + ` rgba(0, 0, 0, 0.45) ${linePx}px, rgba(0, 0, 0, 0) ${linePx}px,`
            + ` rgba(0, 0, 0, 0) ${periodPx}px)`,
          mixBlendMode: 'multiply',
          opacity: roundHalfUp(VHS_SCANLINE_BASE_OPACITY + VHS_SCANLINE_INTENSITY_RANGE * i, 4),
        },
      });
      layers.push({
        id: `${effect.id}-vhs-chroma`,
        kind,
        style: {
          backgroundImage:
            'linear-gradient(90deg, rgba(255, 0, 72, 1) 0%, rgba(255, 0, 72, 0) 9%,'
            + ' rgba(0, 170, 255, 0) 91%, rgba(0, 170, 255, 1) 100%)',
          mixBlendMode: 'screen',
          opacity: roundHalfUp(VHS_CHROMA_BASE_OPACITY + VHS_CHROMA_INTENSITY_RANGE * i, 4),
        },
      });
      layers.push({
        id: `${effect.id}-vhs-noise`,
        kind,
        style: {
          backgroundImage: noiseBackgroundImage(effectRandomInt(seed, ctx.frame, 9) % 1000),
          backgroundSize: `${GRAIN_TILE_PX}px ${GRAIN_TILE_PX}px`,
          mixBlendMode: 'overlay',
          opacity: roundHalfUp(VHS_NOISE_BASE_OPACITY + VHS_NOISE_INTENSITY_RANGE * i, 4),
        },
      });
    }
  }

  if (transforms.length === 0 && layers.length === 0) return EFFECTS_IDENTITY;
  return { transform: transforms.join(' '), layers };
}
